import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

/* ── Alt-theme CSS (scoped to .alt-theme, cannot leak to other routes) ── */
import '../../components/alt/alt-theme.css';
import '../../components/alt/AltAnnouncementBar.css';
import '../../components/alt/AltNav.css';
import '../../components/alt/AltHero.css';
import '../../components/alt/AltIndustryShowcase.css';
import '../../components/alt/AltNicheScrollerSection.css';
import '../../components/alt/AltFeatureCarouselSection.css';
import '../../components/alt/AltNicheDrum.css';
import '../../components/alt/AltAIStackedScroll.css';
import '../../components/alt/AltAISection.css';
import '../../components/alt/AltStackedScrollCards.css';
import '../../components/alt/AltPlatformBar.css';
import '../../components/alt/AltStatsBar.css';
import '../../components/alt/AltFeatures.css';
import '../../components/alt/AltCreatorShowcase.css';
import '../../components/alt/AltMissionSection.css';
import '../../components/alt/AltReferralSection.css';
import '../../components/alt/AltReviews.css';
import '../../components/alt/AltNiches.css';
import '../../components/alt/AltFAQ.css';
import '../../components/alt/AltFinalCTA.css';
import '../../components/alt/AltFooterNew.css';

import AltAnnouncementBar  from '../../components/alt/AltAnnouncementBar';
import AltNav              from '../../components/alt/AltNav';
import AltHero             from '../../components/alt/AltHero';
import AltIndustryShowcase from '../../components/alt/AltIndustryShowcase';
import AltNicheScrollerSection from '../../components/alt/AltNicheScrollerSection';
import AltFeatureCarouselSection   from '../../components/alt/AltFeatureCarouselSection';
import AltFeatureCarouselSectionV2 from '../../components/alt/AltFeatureCarouselSectionV2';
import AltNicheDrum           from '../../components/alt/AltNicheDrum';
import AltAIStackedScroll     from '../../components/alt/AltAIStackedScroll';
import AltAISection           from '../../components/alt/AltAISection';
import AltStackedScrollCards from '../../components/alt/AltStackedScrollCards';
import AltPlatformBar      from '../../components/alt/AltPlatformBar';
import AltStatsBar         from '../../components/alt/AltStatsBar';
import AltFeatures         from '../../components/alt/AltFeatures';
import AltCreatorShowcase  from '../../components/alt/AltCreatorShowcase';
import AltMissionSection   from '../../components/alt/AltMissionSection';
import AltReferralSection  from '../../components/alt/AltReferralSection';
import AltReviews          from '../../components/alt/AltReviews';
import AltNiches           from '../../components/alt/AltNiches';
import AltFAQ              from '../../components/alt/AltFAQ';
import AltFinalCTA         from '../../components/alt/AltFinalCTA';
import AltFooterNew        from '../../components/alt/AltFooterNew';

/* ── Hero poster ────────────────────────────────────────────────────────
   CRA returns the processed URL string directly from a require() of an
   image file. We guard with try/catch in case the loader differs.       */
let heroPoster = '';
try {
  const mod = require('../../images/3.webp');
  heroPoster = typeof mod === 'string' ? mod : (mod?.default ?? '');
} catch (_) {}

export default function AlternativeHomepage() {
  /* The AI stacked-scroll section pins via position: sticky. That breaks when an
     ancestor has `overflow-x: hidden` (the spec then forces overflow-y to `auto`,
     making it a scroll container). The global .app / html / body rules use
     overflow-x: hidden — so while THIS route is mounted we swap them to
     `overflow-x: clip`, which clips horizontally but does NOT create a scroll
     container, so sticky works. Reverted on unmount → live site is untouched. */
  useEffect(() => {
    const targets = [
      document.documentElement,
      document.body,
      document.querySelector('.app'),
    ].filter(Boolean);
    const prev = targets.map((el) => el.style.overflowX);
    targets.forEach((el) => { el.style.overflowX = 'clip'; });
    return () => {
      targets.forEach((el, i) => { el.style.overflowX = prev[i]; });
    };
  }, []);

  return (
    <div className="alt-theme">
      <Helmet>
        <title>Premsi — Create. Enjoy. Share. Earn.</title>
        <meta
          name="description"
          content="Sell and market digital content in seconds. Premsi lets you easily sell Videos, Pics, PDFs and Groups."
        />
        <link rel="canonical" href="https://premsi.com/" />
        <meta property="og:url" content="https://premsi.com/" />
        <link
          href="https://db.onlinewebfonts.com/c/3c14399019df63cb5c3bf794f74080ea?family=Klarheit+Grotesk+Regular"
          rel="stylesheet"
        />
        <link
          href="https://db.onlinewebfonts.com/c/5a743cf8d5935e74a0030381ad71227e?family=FSP+DEMO+-+Universo+Black"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* § 1 — Announcement bar */}
      <AltAnnouncementBar
        message="Refer creators & receive 5% of their spendings for life"
        ctaLabel="Learn more"
        ctaHref="#referral"
      />

      {/* § 2 — Sticky nav */}
      <AltNav />

      {/* § 3 — Hero */}
      <AltHero
        posterSrc={heroPoster || undefined}
        headline="Create. Enjoy. Share. Earn. All in One Place."
        subhead="Your content, your platform. Sell and earn in one place."
        primaryLabel="Get started"
        primaryHref="/invitation"
        secondaryLabel="See how it works"
        secondaryHref="#features"
        trustText="Trusted by 2,000+ creators  ·  $2M paid out"
      />

      {/* § 4 — Industry showcase */}
      <AltIndustryShowcase />

      {/* § 5 — Niche scroller */}
      <AltNicheScrollerSection />

      {/* § 6 — Features carousel 
      <AltFeatureCarouselSection />*/}

      {/* § 6b — Features carousel v2 (inline illustrations, AI-first order) */}
      <AltFeatureCarouselSectionV2 />

      {/* § 6.5 — Niche drum */}
      <AltNicheDrum />

      {/* § 7 — Stacked scroll cards
      <AltStackedScrollCards />*/}

      {/* § 8 — AI pinned stacked scroll */}
      <AltAIStackedScroll />

      {/* § 9 — AI Chat Section */}
      <AltAISection />

      {/* § 9 — Platform strip */}
      <AltPlatformBar />

      {/* § 10 — Stats bar */}
      <AltStatsBar />

      {/* § 6 — Features grid 
      <AltFeatures />*/}

      {/* § 7 — Creator showcase 
      <AltCreatorShowcase />*/}

      {/* § 8 — Mission */}
      <AltMissionSection />

      {/* § 8b — Referral (anchor for announcement bar #referral) */}
      <AltReferralSection />

      {/* § 9 — Reviews 
      <AltReviews />

      * § 10 — Niches *
      <AltNiches />*/}

      {/* § 11 — FAQ accordion */}
      <AltFAQ />

      {/* § 12 — Final CTA 
      <AltFinalCTA />*/}

      {/* § 13 — Footer */}
      <AltFooterNew />
    </div>
  );
}
